// ============================================================================
// BRAND REGISTRY — the one file to edit when adding, removing, or changing
// a domain's branding.
// ============================================================================
//
// HOW TO ADD A NEW DOMAIN:
//   1. Create src/assets/brands/<id>/ with these subfolders and file names
//      (copy src/assets/brands/bluewings/ as a starting template):
//        images/logo.<ext>              — favicon + login/loader logo
//        images/name.svg                 — wordmark shown on login screens
//        images/string_logo.<ext>        — logo on the registration slider
//        images/menu_full_logo.<ext>     — sidebar logo (menu expanded)
//        images/menu_short_logo.<ext>    — sidebar logo (menu collapsed)
//        images/registration_logo.<ext>  — logo on "Register Your Institute"
//        images/login_image.<ext>        — background image on login screens
//        images/registration_bg.<ext>    — background image on registration screens
//        colors/color.css                — CSS variables, same names as
//                                           bluewings' color.css, new values
//        animations/loader.json          — Lottie loading animation
//   2. Add one entry to BRAND_DEFINITIONS below with that id, its real
//      domain, and a display name. If any image isn't a plain .svg (the
//      default), also set its *Ext field to match the real file extension —
//      the app will not find the file otherwise.
//   3. Done. The domain will pick up its brand automatically in production,
//      and can be previewed on localhost via ?brand=<id> (see
//      LOCAL_DEFAULT_BRAND_ID below for what localhost shows by default).
//
// This file only defines WHAT each brand is. HOW a brand gets resolved from
// a hostname (matching, localhost override, fallback) lives in
// brand.config.ts — you shouldn't need to touch that file to manage domains.
//
// LINK-PREVIEW META TAGS (og:title/og:image/etc): most link-unfurling bots
// (WhatsApp, iMessage, Slack, Facebook) fetch raw HTML and never run
// JavaScript, so with one shared build these bots always see whatever
// static defaults are baked into src/index.html — the runtime JS fill in
// assets/js/pre-boot-brand.js only reaches crawlers that execute JS. Real
// per-domain previews for every bot need the web server (nginx/Node/CDN
// edge function, whichever fronts each domain) to rewrite index.html's
// og:*/twitter:* tags based on the incoming Host header before serving —
// that is server configuration, not something this Angular app can do by
// itself from a single build.

export interface BrandDefinition {
  // Short identifier — also the folder name under src/assets/brands/<id>/.
  id: string;
  // Real production domain for this brand, e.g. "bluewings.in". Matched
  // against window.location.hostname with "ends with", so it also covers
  // every subdomain (app.bluewings.in, syn.bluewings.in, etc).
  domainSuffix: string;
  // Human-readable brand name — used for the page <title> and similar.
  name: string;
  // File extension of images/logo.<ext> — omit to default to 'svg'.
  logoExt?: string;
  // File extension of images/string_logo.<ext> — omit to default to 'png'.
  stringLogoExt?: string;
  // File extensions of the two sidebar logos — omit to default to 'png'.
  menuFullLogoExt?: string;
  menuShortLogoExt?: string;
  // File extension of images/registration_logo.<ext> — omit to default to 'png'.
  registrationLogoExt?: string;
  // File extension of images/login_image.<ext> — omit to default to 'png'.
  loginImageExt?: string;
  // File extension of images/registration_bg.<ext> — omit to default to 'png'.
  registrationBgExt?: string;
  // One-line subheading shown under the "Welcome to <brand>" heading on the
  // login screens — omit to default to a generic tagline.
  loginTagline?: string;
  // Description used for link-preview (og:description/twitter:description)
  // meta tags — what shows under the title when the URL is shared on
  // WhatsApp/Slack/etc. Independent from loginTagline so each can be worded
  // for its own audience — omit to default to a generic description. Must
  // also be kept in sync BY HAND with PRE_BOOT_BRANDS in
  // src/assets/js/pre-boot-brand.js and the $brand_description map in
  // deploy/nginx.conf (see that file's header for why).
  metaDescription?: string;
  // Default country for every phone/country-code picker in the app (the
  // intlTelInput widgets, an-country-code-selector's <ngx-country-select>,
  // and the plain <select> in groups.component) — omit to default to India.
  // Dial code with no "+" or leading zeros, e.g. "91", "1", "44".
  defaultCountryDialCode?: string;
  // ISO 3166-1 alpha-2 code, e.g. "IN", "US", "GB" — case varies by which
  // picker library reads it (intlTelInput wants lowercase, an-country-code-
  // selector wants uppercase); BrandConfig exposes both cases so callers
  // never have to re-derive one from the other.
  defaultCountryIso2?: string;
  // URL for the "Help" link shown on the login screens — omit to default to
  // Bluewings' contact page.
  helpUrl?: string;
  // Whether the login screen shows "Welcome to <brand>" + the tagline over
  // the background image — omit to default to true. Set false for a brand
  // whose login background image already has that text baked in, so it
  // isn't shown twice.
  showBrandingText?: boolean;
  // Whether the staff login screen offers "Login with QR Code" as an
  // alternative to password/OTP — omit to default to true (matches the
  // app's original behavior, before this was made brand-configurable). Only
  // takes effect on the staff login flow (the "or" divider + QR button are
  // also gated on isStaffLogin in the template); has no effect on parent login.
  staffQrLoginEnabled?: boolean;
  // Whether the staff edit form shows a "Send Password" button (emails the
  // staff member their login password) — omit to default to true.
  enableSendStaffPassword?: boolean;
  // Whether the admin dashboard shows the "<brand> App Status" tab (Staff
  // App / Parent App adoption cards) — omit to default to true. Set false
  // for a brand that doesn't want to surface app-adoption stats.
  appStatusTabEnabled?: boolean;
  // Whether the school registration flow (new-school-registration) asks for
  // and verifies a mobile OTP — omit to default to true. Set false for a
  // brand that only verifies by email: the mobile OTP input is hidden, the
  // Verify button no longer waits on it, and otpForMobile is sent as null.
  // Only affects registration — has no effect on login OTP elsewhere.
  mobileNumberVerificationWhileRegistration?: boolean;
  // The quote shown on the login screens' right-hand panel (new-login and
  // new-login-with-domain-verification components), under the "Welcome to
  // <brand>" heading — omit to fall back to the app's existing behavior of
  // picking a random quote from the translated LOGIN_QUOTES list.
  loginQuote?: string;
  // Author name shown under loginQuote — only rendered when
  // authorNameDisplay is true. Omit if the brand doesn't want to show an
  // author, or doesn't have one for its quote.
  authorName?: string;
  // Whether to show authorName under loginQuote — omit to default to
  // false (no author line). Has no effect when loginQuote itself is unset
  // (the random LOGIN_QUOTES fallback has its own author per quote).
  authorNameDisplay?: boolean;
  // Which login methods the PARENT portal offers (parent.<domain>/#/parent/login).
  // Both default to true (mobile number OR email, switchable on the login
  // screen). Set one to false to offer only the other method — the method
  // switcher is then hidden. If both are set false the portal falls back to
  // mobile-number login so parents are never locked out by a misconfiguration.
  parentMobileLogin?: boolean;
  parentEmailLogin?: boolean;
  // i18n KEY (not literal text — always rendered through `| translate` at
  // the call site, same as every other label in the app) for the student's
  // national ID number field (e.g. on the admission form, its review step,
  // and the staff add/edit forms) — omit to default to India's
  // 'header.aadharuidNumber'. A non-Indian brand (e.g. Maples, a US-based
  // school) should point this at a generic key instead, such as
  // 'header.idUniqueNumber'.
  nationalIdFieldLabelKey?: string;
  // i18n KEY for the matching document-upload label (e.g. "Aadhar Copy" on
  // the staff add/edit forms) — omit to default to 'groupsV2.aadharCopy'.
  nationalIdCopyLabelKey?: string;
  // i18n KEY for that document's "Upload ..." button text — omit to default
  // to 'groupsV2.uploadAadharCopy'.
  nationalIdCopyUploadLabelKey?: string;
}

// ---- Every domain this app can theme -------------------------------------
export const BRAND_DEFINITIONS: BrandDefinition[] = [
  {
    id: 'bluewings',
    domainSuffix: 'bluewings.in',
    name: 'Bluewings',
    metaDescription: 'Unify your institutionalized education community with Bluewings! Our mobile & cloud-based platform empowers educators, parents, students & staff. Streamline communication, manage students & staff, gain real-time insights - all in one place.',
    loginTagline: 'Platform for Educational Institutions',
    helpUrl: 'https://bluewings.in/#contact',
    loginQuote: 'Education is the passport to the future, for tomorrow belongs to those who prepare for it today.',
    authorName: 'Malcolm X',
    authorNameDisplay: true,
  },
  {
    id: 'maples',
    domainSuffix: 'maples.adyaksharlabs.com',
    name: 'Maples',
    logoExt: 'svg',
    stringLogoExt: 'svg',
    menuFullLogoExt: 'png',
    menuShortLogoExt: 'png',
    registrationLogoExt: 'svg',
    loginTagline: 'AI-Powered Platform for Managing Educational Logistics of Schools',
    metaDescription: 'AI-Powered Platform for Managing Educational Logistics of Schools',
    defaultCountryDialCode: '1',
    defaultCountryIso2: 'US',
    helpUrl: 'https://adyaksharlabs.com/',
    showBrandingText: true,
    staffQrLoginEnabled : false,
    enableSendStaffPassword : true,
    appStatusTabEnabled: false,
    loginQuote: 'A great education doesn’t just prepare students for tomorrow; it inspires them to dream bigger, think differently, and make a difference.',
    authorName: 'Malcolm X',
    authorNameDisplay: false,
    parentMobileLogin : false,
    nationalIdFieldLabelKey: 'header.idUniqueNumber',
    nationalIdCopyLabelKey: 'groupsV2.idCopy',
    nationalIdCopyUploadLabelKey: 'groupsV2.uploadIdCopy',
    mobileNumberVerificationWhileRegistration : false
  },
];

// ---- What localhost / 127.0.0.1 shows by default --------------------------
// Change this one line to switch what local development renders when no
// ?brand=<id> override is active (see captureBrandOverride in
// brand.config.ts — visiting e.g. http://localhost:4200/?brand=bluewings
// switches it for the rest of the session, saved in localStorage).
export const LOCAL_DEFAULT_BRAND_ID = 'maples';
